import posthog from 'posthog-js';
import { setupErrorTracking, cleanupErrorTracking } from './posthogErrorTracking';
import { setupAxiosInterceptor, ejectAxiosInterceptor } from './posthogAxiosInterceptor';

// Re-export so callers can import captureReactError from the single entry point
export { captureReactError } from './posthogErrorTracking';
export { ejectAxiosInterceptor } from './posthogAxiosInterceptor';

/** Returns the current time as a UTC ISO-8601 string. */
export const utcNow = () => new Date().toISOString();

let _initialized = false;

export const getStoredPostHogShopDetails = () => {
    try {
        return JSON.parse(localStorage.getItem('apex-saas_details')) ?? null;
    } catch {
        return null;
    }
};

const cleanProperties = (properties) => Object.fromEntries(Object.entries(properties).filter(([, v]) => v !== undefined && v !== null && v !== ''));

/**
 * Identifies the logged-in shop owner in PostHog.
 * Safe to call on every login — PostHog deduplicates identity calls.
 *
 * @param {object} shopDetails  The shop object stored in localStorage / Redux.
 */
export const setupPostHogIdentity = (shopDetails) => {
    if (!shopDetails) return;

    // Handle both casing variants that appear in the codebase
    const shopOwnerId = shopDetails.shopOwnerId ?? shopDetails.shopOWnerId;
    const shopName = shopDetails.shopName ?? shopDetails.name;

    if (!shopOwnerId) {
        console.warn('[PostHog] setupPostHogIdentity: shopOwnerId is missing from shopDetails.');
        return;
    }

    // 1. Identify the user
    posthog.identify(
        shopOwnerId,
        cleanProperties({
            $name: shopName,
            name: shopName,
            shopName,
            shopId: shopDetails._id,
            posthog_identity_type: 'shop_owner',
        })
    );

    // 2. Set person-level properties (persisted across sessions in PostHog)
    posthog.setPersonProperties?.(
        cleanProperties({
            $name: shopName,
            name: shopName,
            shopName,
            shopId: shopDetails._id,
        })
    );

    // 3. Group analytics — every subsequent event is tied to this shop
    posthog.group(
        'shop',
        shopDetails._id,
        cleanProperties({
            name: shopName,
            shopName,
        })
    );

    // 4. Register super-properties — appended to every event automatically
    posthog.register(
        cleanProperties({
            shopId: shopDetails._id,
            shopName,
            shop_owner_id: shopOwnerId,
        })
    );

    // 5. Emit an identification event for debugging / funnels
    posthog.capture(
        'shop_identified',
        cleanProperties({
            shopId: shopDetails._id,
            shopName,
            shop_owner_id: shopOwnerId,
            timestamp_utc: utcNow(),
        })
    );
};

// ─── Initialisation ───────────────────────────────────────────────────────────

/**
 * @param {import('axios').AxiosInstance} [axiosInstance]
 *   Pass your app's Axios instance to enable API error tracking.
 *   Omit (or pass null) to skip the interceptor.
 
 */
export const initPostHog = (axiosInstance = null) => {
    if (_initialized) {
        console.warn('[PostHog] initPostHog() called more than once — skipping duplicate init.');
        return;
    }

    const apiKey = process.env.REACT_APP_POSTHOG_KEY;
    const apiHost = process.env.REACT_APP_POSTHOG_HOST;

    if (!apiKey) {
        console.warn('[PostHog] REACT_APP_POSTHOG_KEY is not set — PostHog will not be initialised.');
        return;
    }

    // ── Core PostHog init ────────────────────────────────────────────────────
    posthog.init(apiKey, {
        api_host: apiHost ?? 'https://app.posthog.com',

        capture_pageview: false,
        capture_pageleave: true,

        session_recording: {
            maskAllInputs: false,
            maskTextSelector: '.ph-mask',
        },

        // Attach UTC timestamp to every event as a super-property
        loaded: (ph) => {
            ph.register({ timestamp_utc: utcNow() });

            const storedDetails = getStoredPostHogShopDetails();
            if (storedDetails) {
                setupPostHogIdentity(storedDetails);
            }
        },
    });

    // ── Sub-module setup ─────────────────────────────────────────────────────
    // setupErrorTracking();
    // Idle and hidden-tab handling is left to PostHog's recorder. Stopping and restarting
    // recording ourselves forced a full-page snapshot on every resume, freezing heavy pages.

    if (axiosInstance) {
        setupAxiosInterceptor(axiosInstance);
    }

    _initialized = true;
};

/**
 * Tears down all PostHog tracking listeners.
 * Useful for testing or when unmounting the entire React tree.
 *
 * @param {import('axios').AxiosInstance} [axiosInstance]
 */
export const cleanupPostHog = (axiosInstance = null) => {
    cleanupErrorTracking();

    if (axiosInstance) {
        ejectAxiosInterceptor(axiosInstance);
    }

    _initialized = false;
};

export default posthog;
